import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Snackbar, SnackbarContent, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Navigate, useParams } from 'react-router-dom';
import { Sidebar } from '../../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../../Home/sidebarSections';
import { NAV_ROUTES } from '../../Home/navRoutes';
import { TopBar } from '../../IndividualPlan/TopBar';
import {
  confirmSisMatching,
  fetchSisMatchingDistrict,
  MatchingChangedError,
  NoFeedError,
  putSisField,
  putSisValue,
  type FieldChoiceBody,
  type SisDistrict,
} from './sisMatchingApi';
import { formatShortDate, formatSyncedAt, plural, saveFailureMessage, staleSummaryNote, statusPill, withColumnChoice, withMeaning, withNotInSis } from './sisMatchingView';
import { SIS, sisText } from './sisTokens';
import { SisPill } from './SisPill';
import { FieldList } from './FieldList';
import { FieldPanel } from './FieldPanel';
import { HEIGHT_BELOW_BARS } from '../../../core/bannerOffset';

export type DistrictActions = {
  putField: (districtId: string, field: string, choice: FieldChoiceBody, revision: number) => Promise<SisDistrict>;
  putValue: (districtId: string, field: string, column: string, value: string, meaning: string | null, revision: number) => Promise<SisDistrict>;
  confirm: (districtId: string, revision: number) => Promise<SisDistrict>;
};

type Props = {
  source?: (districtId: string) => Promise<SisDistrict>;
  actions?: DistrictActions;
};

const defaultActions: DistrictActions = { putField: putSisField, putValue: putSisValue, confirm: confirmSisMatching };

const LOAD_FAILED = 'Couldn’t load this district. Refresh to try again.';
const REFRESHED = 'This district’s matching changed. Refreshed.';

export function AdminSisMatchingDistrictPage({ source = fetchSisMatchingDistrict, actions = defaultActions }: Props = {}) {
  const { districtId = '', field } = useParams();
  return <DistrictPage key={districtId} districtId={districtId} fieldParam={field} source={source} actions={actions} />;
}

type PageProps = {
  districtId: string;
  fieldParam?: string;
  source: (districtId: string) => Promise<SisDistrict>;
  actions: DistrictActions;
};

type Save = {
  patch: (district: SisDistrict) => SisDistrict;
  save: (revision: number) => Promise<SisDistrict>;
  done?: (next: SisDistrict) => void;
};

function DistrictPage({ districtId, fieldParam, source, actions }: PageProps) {
  const [district, setDistrict] = useState<SisDistrict | null>(null);
  const [failed, setFailed] = useState(false);
  const [noFeed, setNoFeed] = useState(false);
  const [pending, setPending] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const chain = useRef<Promise<void>>(Promise.resolve());
  const revision = useRef(0);
  const settled = useRef<SisDistrict | null>(null);
  const generation = useRef(0);
  const queued = useRef(0);
  const listPath = NAV_ROUTES.sisMatchingAdmin;
  const pathFor = useCallback((key: string) => `${listPath}/${encodeURIComponent(districtId)}/${key}`, [listPath, districtId]);

  const adopt = useCallback((next: SisDistrict) => {
    revision.current = next.revision;
    settled.current = next;
    setDistrict(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    source(districtId)
      .then((result) => {
        if (!cancelled) adopt(result);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof NoFeedError) setNoFeed(true);
        else setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [source, districtId, adopt]);

  const reload = async () => {
    try {
      adopt(await source(districtId));
    } catch (error) {
      if (error instanceof NoFeedError) setNoFeed(true);
      else setDistrict(settled.current);
    }
  };

  const enqueue = ({ patch, save, done }: Save) => {
    const own = generation.current;
    setDistrict((current) => (current ? patch(current) : current));
    queued.current += 1;
    setPending((n) => n + 1);
    chain.current = chain.current.then(async () => {
      try {
        if (generation.current !== own) return;
        const next = await save(revision.current);
        if (generation.current !== own) return;
        revision.current = next.revision;
        settled.current = next;
        if (queued.current === 1) {
          setDistrict(next);
          done?.(next);
        }
      } catch (error) {
        if (generation.current !== own) return;
        generation.current += 1;
        if (error instanceof MatchingChangedError && error.current) {
          adopt(error.current);
          setToast(REFRESHED);
        } else if (error instanceof NoFeedError) {
          setNoFeed(true);
        } else {
          setToast(saveFailureMessage(error));
          await reload();
        }
      } finally {
        queued.current -= 1;
        setPending((n) => n - 1);
      }
    });
  };

  if (noFeed) return <Navigate to={listPath} replace />;
  if (district && !fieldParam) return <Navigate to={pathFor(district.firstOpenField)} replace />;

  const field = district?.fields.find((f) => f.key === fieldParam) ?? district?.fields.find((f) => f.key === district.firstOpenField) ?? district?.fields[0];

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: SIS.background }}>
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box component="main" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar crumbs={[{ label: 'SIS matching', to: listPath }, { label: district?.districtName ?? '' }]} />
        <Box sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {failed && <Typography sx={{ ...sisText.body1, color: SIS.muted }}>{LOAD_FAILED}</Typography>}
          {!failed && district === null && <CircularProgress data-testid="sis-district-loading" size={24} />}
          {district && field && (
            <>
              <Head
                district={district}
                saving={pending > 0}
                onConfirm={() =>
                  enqueue({
                    patch: (d) => d,
                    save: (rev) => actions.confirm(districtId, rev),
                    done: (next) => setToast(`Matching confirmed for ${next.districtName}. Its Program Evaluation is unlocked.`),
                  })
                }
              />
              <Box data-testid="sis-summary" sx={{ display: 'flex', gap: '32px' }}>
                <Stat value={`${district.counts.answered} of ${district.fields.length}`} label="fields answered" />
                <Stat value={district.counts.openValues.toLocaleString('en-US')} label="values need a meaning" warn={district.counts.openValues > 0} />
                <Stat value={String(district.counts.notInSis)} label="not in this SIS" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
                <FieldList district={district} active={field.key} pathFor={pathFor} />
                <FieldPanel
                  key={field.key}
                  district={district}
                  field={field}
                  onColumn={(f, column) => enqueue({ patch: (d) => withColumnChoice(d, f.key, column), save: (rev) => actions.putField(districtId, f.key, { column }, rev) })}
                  onNotInSis={(f) => enqueue({ patch: (d) => withNotInSis(d, f.key), save: (rev) => actions.putField(districtId, f.key, { notInSis: true }, rev) })}
                  onMeaning={(f, value, meaning) =>
                    enqueue({
                      patch: (d) => withMeaning(d, f.key, value.key, meaning || null),
                      save: (rev) => actions.putValue(districtId, f.key, f.choice.column ?? '', value.key, meaning || null, rev),
                    })
                  }
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Snackbar open={toast !== null} autoHideDuration={6000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <SnackbarContent message={toast ?? ''} sx={{ fontFamily: SIS.font, bgcolor: SIS.ink }} />
      </Snackbar>
    </Box>
  );
}

function Stat({ value, label, warn }: { value: string; label: string; warn?: boolean }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography sx={{ ...sisText.h3, color: warn ? SIS.warningDark : SIS.ink, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      <Typography sx={{ ...sisText.body2, color: warn ? SIS.warningDark : SIS.muted }}>{label}</Typography>
    </Box>
  );
}

function confirmedSuffix(district: SisDistrict): string {
  if (district.matching === 'confirmed') return ' · Program Evaluation unlocked';
  if (district.counts.newValues > 0) return ` · ${plural(district.counts.newValues, 'new value')} since`;
  return ' · changed since';
}

function Head({ district, saving, onConfirm }: { district: SisDistrict; saving: boolean; onConfirm: () => void }) {
  const confirmation = district.confirmation;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Typography component="h1" sx={sisText.h1}>{district.districtName}</Typography>
          <SisPill pill={statusPill(district.matching)} />
        </Box>
        <Typography sx={{ ...sisText.body1, color: SIS.muted }}>
          {`RosterStream feed connected ${formatShortDate(district.feed.boundAt, true)} · last synced ${district.feed.syncedAt ? formatSyncedAt(district.feed.syncedAt) : 'not yet'} · ${plural(district.feed.students, 'student')} · ${plural(district.feed.columns, 'column')}`}
        </Typography>
        {district.summaryAsOf && <Typography sx={{ ...sisText.body1, color: SIS.warningDark }}>{staleSummaryNote(district.summaryAsOf)}</Typography>}
        {confirmation && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px' }}>
            <CheckCircleOutlineIcon aria-hidden="true" sx={{ width: 20, height: 20, color: SIS.disabled }} />
            <Typography sx={sisText.body1}>{`Confirmed by ${confirmation.byName || confirmation.by} on ${formatShortDate(confirmation.at, true)}${confirmedSuffix(district)}`}</Typography>
          </Box>
        )}
      </Box>
      {district.matching !== 'confirmed' && (
        <Button variant="contained" onClick={onConfirm} disabled={saving || !district.counts.complete}>
          Confirm matching
        </Button>
      )}
    </Box>
  );
}
